import React, { useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import { Select, Button, Input, Form } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateEnvelopeStatusSchema } from "../../schema/envelope.schema";

const { Option } = Select;
const { TextArea } = Input;

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 8 },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 8, offset: 6 },
    sm: { span: 8, offset: 6 },
  },
};

const getStatusOptions = (envelopId, list) => {
  const selectedEnvelope = list.find((el) => el.Id === envelopId);
  const options = [];
  if (selectedEnvelope) {
    if (
      ["FAILED_DOWNLOAD", "FAILED_UPLOAD"].includes(selectedEnvelope.status)
    ) {
      options.push({ label: "Completed", value: "COMPLETED" });
    }
    if (
      ["FAILED_DOWNLOAD", "FAILED_UPLOAD", "PENDING", "SENT_FOR_SIGNING", "COMPLETED"].includes(
        selectedEnvelope.status,
      )
    ) {
      options.push({ label: "Voided", value: "VOID" });
    }
  }
  return options;
};

const UpdateEnvelopStatus = forwardRef((props, ref) => {
  const { filteredRecords, sendCompleteStatus, loadingUpdateEnvelopStatus } =
    props;

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(updateEnvelopeStatusSchema),
  });

  useImperativeHandle(ref, () => ({
    reset,
  }));

  return (
    <div>
      <form onSubmit={handleSubmit(sendCompleteStatus)}>
        <Form.Item
          {...formItemLayout}
          label="Rev ID"
          help={errors?.revId?.message ?? ""}
          validateStatus={`${errors.revId ? "error" : ""}`}
          required
        >
          <Controller
            name="revId"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Select
                placeholder="Please Select"
                style={{ width: "100%" }}
                onChange={(e) => {
                  onChange(e);
                  setValue("status", undefined);
                }}
                onBlur={onBlur}
                value={value}
                showSearch
              >
                {filteredRecords
                  .filter((item) =>
                    [
                      "FAILED_DOWNLOAD",
                      "FAILED_UPLOAD",
                      "PENDING",
                      "SENT_FOR_SIGNING",
                      "COMPLETED",
                    ].includes(item.status),
                  )
                  .map((elem) => {
                    return (
                      <Option key={elem.Id} value={elem.Id}>
                        {elem.Id}
                      </Option>
                    );
                  })}
              </Select>
            )}
          />
        </Form.Item>
        <Form.Item
          {...formItemLayout}
          label="Update Envelope Status"
          help={errors?.status?.message ?? ""}
          validateStatus={`${errors.status ? "error" : ""}`}
          required
        >
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                placeholder="Please Select"
                style={{ width: "100%" }}
                {...field}
              >
                {getStatusOptions(watch("revId"), filteredRecords).map(
                  (elem) => (
                    <Option key={elem.value} value={elem.value}>
                      {elem.label}
                    </Option>
                  ),
                )}
              </Select>
            )}
          />
        </Form.Item>
        <Form.Item
          {...formItemLayout}
          label="Reason"
          help={errors?.reason?.message ?? ""}
          validateStatus={`${errors.reason ? "error" : ""}`}
          required
        >
          <Controller
            name="reason"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextArea
                onChange={(e) => onChange(e.target.value)}
                value={value}
                onBlur={onBlur}
                allowClear
              />
            )}
          />
        </Form.Item>

        <Form.Item {...formItemLayoutWithOutLabel}>
          <Button
            loading={loadingUpdateEnvelopStatus}
            htmlType="submit"
            style={{ width: "100%" }}
          >
            Submit
          </Button>
        </Form.Item>
      </form>
    </div>
  );
});

UpdateEnvelopStatus.defaultProps = {
  loadingUpdateEnvelopStatus: false,
};

UpdateEnvelopStatus.propTypes = {
  filteredRecords: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sendCompleteStatus: PropTypes.func.isRequired,
  loadingUpdateEnvelopStatus: PropTypes.bool,
};

export default UpdateEnvelopStatus;
